/**
 * Turns the backend's admission refusals into a redirect to the `/error` page.
 */

const ADMISSION_CODES_BY_STATUS: Record<number, readonly string[]> = {
  403: ['NOT_PROVISIONED', 'ACCOUNT_DEACTIVATED'],
  503: ['PROVISIONING_UNAVAILABLE'],
};

/** Message shown when a refusal arrives without one of its own. */
const FALLBACK_MESSAGE: Record<string, string> = {
  NOT_PROVISIONED: 'Your account is not set up in Nebari.',
  ACCOUNT_DEACTIVATED: 'Your Nebari account has been deactivated.',
  PROVISIONING_UNAVAILABLE: 'Sign-in is temporarily unavailable. Please try again.',
};

export const ERROR_ROUTE = '/error';

/** Thrown after an admission redirect so the caller stops. */
export class AdmissionDeniedError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'AdmissionDeniedError';
  }
}

interface AdmissionBody {
  code?: unknown;
  message?: unknown;
}

function isAdmissionBody(body: unknown): body is AdmissionBody {
  return typeof body === 'object' && body !== null;
}

/**
 * The `/error` URL for an admission refusal, or null when the response is not
 * one or the page is already `/error`.
 */
export function admissionRedirectFor(
  status: number,
  body: unknown,
  currentPath: string,
): { code: string; message: string; href: string } | null {
  const expected = ADMISSION_CODES_BY_STATUS[status];
  if (!expected || !isAdmissionBody(body)) return null;
  const code = expected.find((c) => c === body.code);
  if (!code) return null;
  if (currentPath === ERROR_ROUTE || currentPath.startsWith(`${ERROR_ROUTE}/`)) return null;
  const message =
    typeof body.message === 'string' && body.message.trim() ? body.message : FALLBACK_MESSAGE[code];
  const params = new URLSearchParams({ error: 'access_denied', error_description: message });
  return { code, message, href: `${ERROR_ROUTE}?${params}` };
}
